import React, { useState } from 'react';
import { Card, Button, Badge, Carousel, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBed, 
  faBath, 
  faRuler, 
  faHeart,
  faCalendarAlt,
  faMapMarkerAlt,
  faChevronDown,
  faChevronUp,
  faPhone,
  faBuilding,
  faStar,
  faThumbsUp,
  faThumbsDown,
  faTrophy
} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular, faThumbsUp as faThumbsUpRegular, faThumbsDown as faThumbsDownRegular } from '@fortawesome/free-regular-svg-icons';
import './PropertyList.css';
import axiosInstance from '../../helpers/api';
import { Property } from '../../types/property';
import { isPropertyAddress, safeFormatPrice, getAddressString, getCity, getState, getPropertyId } from '../../types/propertyUtils';

interface PropertyListProps {
  properties: Property[];
  selectedProperty?: Property | null;
  onPropertySelect: (property: Property) => void;
  onScheduleTour?: (property: Property) => void;
  onSaveProperty?: (property: Property) => void;
  savedPropertyIds?: Set<string>;
}

const PropertyList: React.FC<PropertyListProps> = ({
  properties,
  selectedProperty,
  onPropertySelect,
  onScheduleTour,
  onSaveProperty,
  savedPropertyIds = new Set()
}) => {
  const [expandedProperty, setExpandedProperty] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState<{ [key: string]: number }>({});
  const [propertyFeedback, setPropertyFeedback] = useState<{ [key: string]: 'positive' | 'negative' | null }>({});

  const toggleExpanded = (propertyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedProperty(expandedProperty === propertyId ? null : propertyId);
  };

  const handleSave = (property: Property, e: React.MouseEvent) => {
    e.stopPropagation();
    onSaveProperty?.(property);
  };

  const handleFeedback = async (property: Property, feedback: 'positive' | 'negative', e: React.MouseEvent) => {
    e.stopPropagation();

    const propId = getPropertyId(property);
    const newFeedback = propertyFeedback[propId] === feedback ? null : feedback;

    // Update local state
    setPropertyFeedback({
      ...propertyFeedback,
      [propId]: newFeedback
    });

    // Send feedback to backend
    try {
      await axiosInstance.post('/property-feedback/feedback', {
        propertyId: propId,
        mlsNumber: property.mlsNumber,
        feedback: newFeedback || feedback,
        searchQuery: sessionStorage.getItem('lastSearchQuery') || null,
        searchCriteria: sessionStorage.getItem('lastSearchCriteria') || null
      });
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
  };

  const getRelevanceStars = (score: number) => {
    return Math.round(score / 20); // Convert 0-100 to 0-5 stars
  };

  return (
    <div className="property-list">
      {properties.map((property) => {
        const propId = getPropertyId(property);
        return (
        <Card
          key={propId}
          className={`property-list-card ${selectedProperty && getPropertyId(selectedProperty) === propId ? 'selected' : ''}`}
          onClick={() => onPropertySelect(property)}
        >
          <div className="property-list-image">
            {property.images && property.images.length > 1 ? (
              <Carousel
                activeIndex={carouselIndex[propId] || 0}
                onSelect={(index) => setCarouselIndex({...carouselIndex, [propId]: index})}
                interval={null}
                indicators={false}
                onClick={(e) => e.stopPropagation()}
              >
                {property.images.slice(0, 5).map((image, index) => (
                  <Carousel.Item key={index}>
                    <img
                      className="d-block w-100"
                      src={image}
                      alt={`${getAddressString(property)} - Image ${index + 1}`}
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/400x300/1f2937/ffffff?text=No+Image';
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <img
                src={property.images?.[0] || property.thumbnails?.[0] || 'https://via.placeholder.com/400x300/1f2937/ffffff?text=No+Image'}
                alt={getAddressString(property)}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/400x300/1f2937/ffffff?text=No+Image';
                }}
              />
            )}
            
            {/* Badges and Controls Overlay */}
            <div className="property-image-overlay">
              <div className="overlay-left">
                {property.daysOnMarket && property.daysOnMarket < 3 && (
                  <Badge bg="danger" className="new-badge">NEW</Badge>
                )}
              </div>
              <div className="overlay-right">
                <div className="property-specs-badge">
                  <FontAwesomeIcon icon={faBed} className="me-1" />
                  {property.bedrooms || 0}
                  <span className="mx-2">|</span>
                  <FontAwesomeIcon icon={faBath} className="me-1" />
                  {property.bathrooms || 0}
                  <span className="mx-2">|</span>
                  <FontAwesomeIcon icon={faRuler} className="me-1" />
                  {property.sqft?.toLocaleString() || '0'}
                </div>
                {onSaveProperty && (
                  <Button
                    variant={savedPropertyIds.has(propId) ? "danger" : "light"}
                    size="sm"
                    className="save-btn"
                    onClick={(e) => handleSave(property, e)}
                    title={savedPropertyIds.has(propId) ? "Remove from favorites" : "Save to favorites"}
                  >
                    <FontAwesomeIcon
                      icon={savedPropertyIds.has(propId) ? faHeart : faHeartRegular}
                    />
                  </Button>
                )}
              </div>
            </div>
            {/* Photo Count Badge - Bottom */}
            {property.images && property.images.length > 1 && (
              <Badge bg="dark" className="photo-count-badge">
                {(carouselIndex[propId] || 0) + 1} / {Math.min(property.images.length, 5)}
              </Badge>
            )}
          </div>
          
          <Card.Body>
            <div className="property-list-header">
              <div>
                <h5 className="property-price">{safeFormatPrice(property.price)}</h5>
                {/* Relevance Indicators */}
                {property.isTopMatch && (
                  <Badge bg="danger" className="ms-2">
                    <FontAwesomeIcon icon={faTrophy} className="me-1" />
                    Best Match
                  </Badge>
                )}
                {property.relevanceScore !== undefined && !property.isTopMatch && (
                  <Badge bg={property.relevanceScore >= 80 ? 'success' : property.relevanceScore >= 60 ? 'warning' : 'secondary'} className="ms-2">
                    {property.relevanceScore}% Match
                  </Badge>
                )}
                {property.ranking && property.ranking <= 3 && !property.isTopMatch && (
                  <Badge bg="info" className="ms-2">#{property.ranking}</Badge>
                )}
              </div>
            </div>

            {/* Relevance Stars */}
            {property.relevanceScore !== undefined && (
              <div className="text-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon 
                    key={i} 
                    icon={faStar} 
                    className={i < getRelevanceStars(property.relevanceScore || 0) ? 'text-warning' : 'text-muted'} 
                  />
                ))}
              </div>
            )}

            <p className="property-address">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
              {getAddressString(property)}
            </p>
            
            {/* Courtesy of and Days on Market */}
            <div className="property-meta">
              {(property.listingOffice || property.listingAgent?.officeName) && (
                <small className="courtesy-text">
                  Courtesy of {property.listingOffice || property.listingAgent?.officeName}
                </small>
              )}
              <div className="property-meta-row">
                <span className="mls-number">
                  MLS# {property.mlsNumber}
                </span>
                {property.daysOnMarket !== undefined && (
                  <>
                    <span className="separator">•</span>
                    <span className="days-on-market">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {property.daysOnMarket} days
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Feedback Buttons */}
            <div className="d-flex justify-content-center gap-2 my-2">
              <Button
                variant={propertyFeedback[propId] === 'positive' ? 'success' : 'outline-success'}
                size="sm"
                onClick={(e) => handleFeedback(property, 'positive', e)}
                className="d-flex align-items-center gap-1"
              >
                <FontAwesomeIcon icon={propertyFeedback[propId] === 'positive' ? faThumbsUp : faThumbsUpRegular} />
                <span>Helpful</span>
              </Button>
              <Button
                variant={propertyFeedback[propId] === 'negative' ? 'danger' : 'outline-danger'}
                size="sm"
                onClick={(e) => handleFeedback(property, 'negative', e)}
                className="d-flex align-items-center gap-1"
              >
                <FontAwesomeIcon icon={propertyFeedback[propId] === 'negative' ? faThumbsDown : faThumbsDownRegular} />
                <span>Not Helpful</span>
              </Button>
            </div>

            {/* Fair Housing Logo */}
            <div className="fair-housing-container">
              <svg 
                className="fair-housing-logo" 
                viewBox="0 0 100 50" 
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Equal Housing Opportunity"
              >
                {/* House outline */}
                <path d="M25 5 L5 20 L5 35 L20 35 L20 25 L30 25 L30 35 L45 35 L45 20 Z" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"/>
                {/* Roof */}
                <path d="M25 5 L45 20 M25 5 L5 20" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"/>
                {/* Equal sign */}
                <line x1="50" y1="18" x2="60" y2="18" stroke="currentColor" strokeWidth="2"/>
                <line x1="50" y1="25" x2="60" y2="25" stroke="currentColor" strokeWidth="2"/>
                {/* Text */}
                <text x="25" y="45" 
                      textAnchor="middle" 
                      fontSize="8" 
                      fill="currentColor"
                      fontFamily="Arial, sans-serif">
                  EQUAL HOUSING OPPORTUNITY
                </text>
              </svg>
            </div>
            
            {/* Action Buttons */}
            <div className="property-action-buttons">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={(e) => toggleExpanded(propId, e)}
                className="expand-btn"
              >
                <FontAwesomeIcon icon={expandedProperty === propId ? faChevronUp : faChevronDown} className="me-1" />
                {expandedProperty === propId ? 'Less' : 'More'} Info
              </Button>
              {onScheduleTour && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onScheduleTour(property);
                  }}
                >
                  Schedule Tour
                </Button>
              )}
            </div>

            {/* Expandable Details */}
            <Collapse in={expandedProperty === propId}>
              <div className="property-expanded-details">
                {/* Property Description */}
                {property.description && (
                  <div className="detail-section">
                    <h6>Description</h6>
                    <p>{property.description}</p>
                  </div>
                )}

                {/* Property Features */}
                {property.features && property.features.length > 0 && (
                  <div className="detail-section">
                    <h6>Features</h6>
                    <ul className="feature-list">
                      {property.features.slice(0, 6).map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Additional Details */}
                <div className="detail-section">
                  <h6>Property Details</h6>
                  <div className="detail-grid">
                    {property.yearBuilt && (
                      <div className="detail-item">
                        <span className="detail-label">Year Built:</span>
                        <span className="detail-value">{property.yearBuilt}</span>
                      </div>
                    )}
                    {property.propertyType && (
                      <div className="detail-item">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">{property.propertyType}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">MLS #:</span>
                      <span className="detail-value">{property.mlsNumber}</span>
                    </div>
                  </div>
                </div>

                {/* Listing Agent Info */}
                {property.listingAgent && (
                  <div className="detail-section agent-info">
                    <h6>Listing Agent</h6>
                    <div className="agent-details">
                      <p className="agent-name">
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                        {property.listingAgent.name}
                      </p>
                      {property.listingAgent.phone && (
                        <p className="agent-phone">
                          <FontAwesomeIcon icon={faPhone} className="me-2" />
                          {property.listingAgent.phone}
                        </p>
                      )}
                      {property.listingAgent.officeName && (
                        <p className="agent-office">
                          {property.listingAgent.officeName}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Collapse>
          </Card.Body>
        </Card>
        );
      })}

      {properties.length === 0 && (
        <div className="no-properties">
          <p>No properties found in this area.</p>
          <small>Try zooming out or adjusting your filters.</small>
        </div>
      )}
      
      {/* MLS Attribution */}
      {properties.length > 0 && (
        <div className="mls-attribution" style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          fontSize: '12px',
          color: '#6c757d',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0 }}>
            Listing information provided courtesy of KairosIQ AI Realty, Inc. 
            The data relating to real estate for sale on this website comes in part from the 
            Internet Data Exchange (IDX) Program of Arizona Regional Multiple Listing Service, Inc. 
            All information deemed reliable but not guaranteed.
          </p>
          <p style={{ margin: '5px 0 0 0' }}>
            © {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc. All rights reserved.
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyList;